import { createSelector, MemoizedSelector } from '@ngrx/store';
import { selectAddresses } from '../../address/selectors/address.selector';
import { selectInProgressConfirmations } from '../../confirmation/selectors/confirmation.selector';
import { selectCompletions, selectNotificationEntries } from '../../notification/selectors/notification.selector';
import { TimesheetState, TimesheetWorkEntryState } from '../timesheet-collection.state';
import { buildTimesheetRows, TimesheetRow, TimesheetWorkRow } from '../utils/build-timesheet-rows';
import { emptyFormModel, TimesheetFormModel, toTimesheetFormModel } from '../utils/timesheet-form-model';
import { hasInProgressConfirmations } from '../utils/submit-gates';
import { deriveInProgressWorkEntries, withInProgressEntries } from '../utils/derive-confirmation-entries';
import { selectTimesheetDerivationConfig } from './confirmation-context.selector';
import {
    selectCurrentTimesheet,
    selectLatestSubmittedEndOfWork,
    selectSelectedTimesheet,
    selectTimesheets,
} from './timesheet.selector';

/**
 * The work entries the open confirmations — and the confirmation-less in-progress notifications —
 * currently amount to.
 *
 * This is the live connection that replaced the copy a confirmation's autosave used to push into the
 * slice: the times a technician has recorded but not yet signed off are read straight from the
 * confirmation slice whenever the timesheet is read, so they cannot be stale, and they are never
 * persisted as though the timesheet owned them.
 *
 * Composed over whole collections rather than per confirmation — see `deriveInProgressWorkEntries`
 * for why a factory selector per notification would defeat memoization here.
 *
 * `now` is read from the clock rather than passed in, the same tradeoff `selectSelectedTimesheetRows`
 * makes in this file: it is only consulted for the open end of a status-derived row, and memoization
 * means it is re-read exactly when one of the other five inputs changes, not on every store read.
 */
export const selectInProgressWorkEntries: MemoizedSelector<object, TimesheetWorkEntryState[]> = createSelector(
    selectInProgressConfirmations,
    selectNotificationEntries,
    selectCompletions,
    selectAddresses,
    selectTimesheetDerivationConfig,
    (confirmations, notifications, completions, addresses, config) =>
        deriveInProgressWorkEntries(
            confirmations,
            notifications,
            completions,
            addresses,
            config,
            new Date().toISOString(),
        ),
);

/**
 * The current timesheet with the open confirmations' entries merged in.
 *
 * Feeds the `TimesheetModel` a confirmation screen is built with: `ConfirmationTimeRecordingModel`
 * reads that timesheet to show the times notifications already occupy, and to hold the confirmation's
 * own. Those entries used to be in the slice because every autosave copied them there,
 * so reading the unmerged slice now would show the technician nothing — and the model's fallback would
 * silently invent times instead of reporting the gap.
 *
 * The confirmation slice therefore feeds a timesheet projection that a confirmation model reads back.
 * That round trip is deliberate and it terminates: the model takes one snapshot as it is constructed,
 * rather than subscribing, so there is no loop to settle.
 *
 * Once the timesheet is submitted it is returned unmerged: a submitted timesheet is a closed record,
 * so a confirmation still being worked on for a later timesheet must not appear on it.
 */
export const selectCurrentTimesheetMerged: MemoizedSelector<object, TimesheetState | undefined> = createSelector(
    selectCurrentTimesheet,
    selectInProgressWorkEntries,
    (timesheet, inProgress) =>
        timesheet ? (timesheet.isSubmitted ? timesheet : withInProgressEntries(timesheet, inProgress)) : undefined,
);

/**
 * The whole collection as the UI reads it: every timesheet the slice holds, with the open
 * confirmations' entries merged into the one still being worked on.
 *
 * Only the open timesheet is merged into. A derived entry displaces the committed entries of the
 * same notification (see {@link withInProgressEntries}), which is right for the timesheet the
 * confirmation belongs to and wrong for an already-submitted one: a notification worked on again
 * today would otherwise blank out the hours it contributed on an earlier day.
 *
 * Returns the collection itself when nothing derives, so readers keep their memo rather than
 * recomputing against an identical copy.
 */
export const selectTimesheetsMerged: MemoizedSelector<object, TimesheetState[]> = createSelector(
    selectTimesheets,
    selectInProgressWorkEntries,
    (timesheets, inProgress): TimesheetState[] =>
        inProgress.length === 0
            ? timesheets
            : timesheets.map((timesheet) =>
                  timesheet.isSubmitted ? timesheet : withInProgressEntries(timesheet, inProgress),
              ),
);

/**
 * The selected timesheet as the UI reads it: what the slice holds, plus the entries of every
 * confirmation still being worked on.
 *
 * Only the read side merges. `selectTimesheets` and `selectCurrentTimesheet` deliberately stay
 * unmerged, because they are what the persistence layer writes and what the submit out model is built
 * from — so derived entries stay out of storage and out of the submitted payload by construction,
 * rather than by every writer remembering to filter them.
 *
 * A submitted timesheet is returned as-is, unmerged: it is a closed record, and a confirmation still
 * being worked on belongs to whichever timesheet is current, not to one already signed off.
 */
export const selectSelectedTimesheetMerged: MemoizedSelector<object, TimesheetState | undefined> = createSelector(
    selectSelectedTimesheet,
    selectInProgressWorkEntries,
    (timesheet, inProgress) =>
        timesheet ? (timesheet.isSubmitted ? timesheet : withInProgressEntries(timesheet, inProgress)) : undefined,
);

/**
 * The rows the UI renders for the selected timesheet: the SOW marker, the entries in
 * chronological order with gap rows over the unaccounted spans, and the EOW marker.
 *
 * Built from the merged timesheet, so a confirmation still being worked on shows the time it occupies
 * instead of a gap — which is what the push these rows used to be built from achieved by copying the
 * entries into the slice.
 *
 * The current instant is read here rather than passed in. It only feeds the end-of-work fallback and
 * the bound ceiling, and memoization means it is re-read exactly when the merged timesheet changes —
 * the same cadence at which the legacy code rebuilt its model and re-evaluated `getEndOfWork()`.
 */
export const selectSelectedTimesheetRows = createSelector(
    selectSelectedTimesheetMerged,
    selectLatestSubmittedEndOfWork,
    (timesheet, latestSubmittedEndOfWork): TimesheetRow[] =>
        timesheet ? buildTimesheetRows(timesheet, latestSubmittedEndOfWork, new Date().toISOString()) : [],
);

/**
 * The rows of the *current* (unsubmitted) timesheet — the direct mirror of
 * {@link selectSelectedTimesheetRows}, but over {@link selectCurrentTimesheetMerged} rather than
 * the one the UI happens to have selected. Feeds the travel-status-update pipeline, which always
 * has to reason about the current timesheet regardless of what the timesheet page is showing.
 */
export const selectCurrentTimesheetRows = createSelector(
    selectCurrentTimesheetMerged,
    selectLatestSubmittedEndOfWork,
    (timesheet, latestSubmittedEndOfWork): TimesheetRow[] =>
        timesheet ? buildTimesheetRows(timesheet, latestSubmittedEndOfWork, new Date().toISOString()) : [],
);

/** The travel-back entry a travel-status-update effect may still shorten, in the plain shape it needs. */
export type LastEditableTravelBackEntry = Readonly<{ entryId: string; start: string; end: string }>;

/**
 * The current timesheet's last settled travel-back work row, if any — replaces
 * `TimesheetModel.getLastTravelBackEntry()`.
 *
 * `find`, not `findLast`: `buildTimesheetRows` already flags `isTravelBackEditable` on only the
 * last settled travel-back work row, which is the same row the legacy
 * `findLast(isTravelBackEntry && isTravelBackEditable)` picked.
 *
 * Projected to `{ entryId, start, end }` so the effect never has to touch a row type.
 */
export const selectLastEditableTravelBackEntry: MemoizedSelector<object, LastEditableTravelBackEntry | undefined> =
    createSelector(selectCurrentTimesheetRows, (rows): LastEditableTravelBackEntry | undefined => {
        const row = rows.find(
            (candidate): candidate is TimesheetWorkRow => candidate.kind === 'work' && candidate.isTravelBackEditable,
        );
        return row ? { entryId: row.entryId, start: row.start, end: row.end } : undefined;
    });

/**
 * The selected timesheet as the form edits it.
 *
 * A projection of the slice, not a copy of it: the page binds its form to this and every edit
 * goes straight back as an action, so what the fields hold and what the rows are drawn from are
 * the same values. It differs from `TimesheetState` only in what a form needs and the slice does
 * not carry — the resolved end of work, and each entry's in-progress flag looked up from its
 * notification.
 */
export const selectSelectedTimesheetFormModel = createSelector(
    selectSelectedTimesheetMerged,
    selectSelectedTimesheetRows,
    (timesheet, rows): TimesheetFormModel => (timesheet ? toTimesheetFormModel(timesheet, rows) : emptyFormModel),
);

/**
 * Whether the selected timesheet holds a confirmation that is still being worked on. Such a
 * timesheet renders its entries (marked in progress) but cannot be submitted.
 */
export const selectSelectedTimesheetHasInProgressConfirmations = createSelector(
    selectSelectedTimesheetMerged,
    (timesheet): boolean => (timesheet ? hasInProgressConfirmations(timesheet) : false),
);
